import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Map,
  Navigation,
  Search,
  Construction,
  Lightbulb,
  Rocket,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { gradients } from "../../utils/theme";

interface InteractiveIndoorMapProps {
  mallName: string;
}

/**
 * InteractiveIndoorMap Component
 *
 * Coming soon feature showcase for indoor navigation:
 * - Store search capability
 * - Turn-by-turn indoor navigation
 * - Real-time location tracking
 * - Multi-floor support
 *
 * Only visible when REACT_APP_SHOW_INDOOR_MAP_FEATURE=true (demo mode)
 */
const InteractiveIndoorMap: React.FC<InteractiveIndoorMapProps> = ({
  mallName,
}) => {
  const theme = useTheme();

  const features = [
    {
      icon: <Search />,
      title: "Store Search",
      description: "Find any store or place instantly",
    },
    {
      icon: <Navigation />,
      title: "Turn-by-Turn Navigation",
      description: "Step-by-step directions to your destination",
    },
    {
      icon: <Map />,
      title: "Multi-Floor Support",
      description: "Navigate across all mall levels seamlessly",
    },
  ];

  return (
    <Card
      sx={{
        position: "relative",
        overflow: "hidden",
        backdropFilter: "blur(10px)",
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.paper,
          0.95
        )} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: gradients.emerald,
        },
      }}
    >
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        {/* Header with Coming Soon Badge */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <Chip
                icon={<Construction sx={{ fontSize: 18 }} />}
                label="COMING SOON"
                sx={{
                  bgcolor: alpha(theme.palette.warning.main, 0.15),
                  color: theme.palette.warning.main,
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                }}
              />
              <Chip
                icon={<Rocket sx={{ fontSize: 18 }} />}
                label="FUTURE FEATURE"
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.15),
                  color: theme.palette.info.main,
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                }}
              />
            </Box>
          </motion.div>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              background: gradients.emerald,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
              fontSize: { xs: "1.75rem", md: "2.125rem" },
            }}
          >
            Interactive Indoor Map
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: { xs: "0.95rem", md: "1rem" },
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Navigate {mallName} like never before with our upcoming indoor
            mapping system
          </Typography>
        </Box>

        {/* Mock Map Visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Box
            sx={{
              position: "relative",
              borderRadius: 3,
              overflow: "hidden",
              height: { xs: 200, md: 280 },
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.05
              )} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
              border: `2px dashed ${alpha(theme.palette.divider, 0.3)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 4,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Lightbulb
                  sx={{
                    fontSize: { xs: 64, md: 80 },
                    color: theme.palette.warning.main,
                    filter: `drop-shadow(0 0 20px ${alpha(
                      theme.palette.warning.main,
                      0.4
                    )})`,
                    mb: 2,
                  }}
                />
              </motion.div>
              <Typography
                variant="h6"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  fontSize: { xs: "1rem", md: "1.25rem" },
                }}
              >
                Innovation in Progress
              </Typography>
            </Box>

            {/* Decorative Grid Pattern */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                opacity: 0.1,
                backgroundImage: `
                  linear-gradient(${theme.palette.divider} 1px, transparent 1px),
                  linear-gradient(90deg, ${theme.palette.divider} 1px, transparent 1px)
                `,
                backgroundSize: "20px 20px",
              }}
            />
          </Box>
        </motion.div>

        {/* Feature Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2,
            mb: 3,
          }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
            >
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.success.main,
                    0.05
                  )} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 8px 20px ${alpha(
                      theme.palette.success.main,
                      0.15
                    )}`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: gradients.emerald,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    mx: "auto",
                    mb: 1.5,
                    boxShadow: `0 4px 12px ${alpha(
                      theme.palette.success.main,
                      0.3
                    )}`,
                  }}
                >
                  {React.cloneElement(feature.icon, { sx: { fontSize: 24 } })}
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    mb: 0.5,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            </motion.div>
          ))}
        </Box>

        {/* CTA Message */}
        <Box
          sx={{
            textAlign: "center",
            p: 2.5,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.info.main,
              0.05
            )} 0%, ${alpha(theme.palette.info.main, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 500,
              mb: 1,
            }}
          >
            🚀 We're building the future of mall navigation!
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
            }}
          >
            Stay tuned for real-time indoor navigation, AR wayfinding, and
            personalized store recommendations
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default InteractiveIndoorMap;
